/**
 * Healthcare Chat Viewer Component
 * Read-only viewer for healthcare_chat_v2 sessions
 * Uses same styling as AskNaturalOrganicChat.tsx
 */

import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageSquare, Leaf } from 'lucide-react';
import { ChatSessionFull } from '../types';

interface HealthcareChatViewerProps {
  session: ChatSessionFull;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  personas_cited?: string[];
  format?: string;
  progress_snapshot?: StructuredProgress;
}

interface StageInfo {
  stage: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
  group?: 'planning' | 'output';
}

interface PlanStepProgress {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'skipped';
}

interface StructuredProgress {
  current_stage: string;
  stages: StageInfo[];
  plan_steps?: PlanStepProgress[];
  plan_version: number;
  message: string;
}

const BRAND_PRIMARY = '#07E5D1';

// Markdown components matching AskNaturalOrganicChat.tsx styling
const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>
  ),
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-[#07E5D1] hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-3">
      <table className="min-w-full border-collapse border border-gray-200 text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-gray-200">{children}</tr>,
  th: ({ children }) => (
    <th className="px-3 py-2 text-left font-semibold text-gray-700 border border-gray-200">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-gray-600 border border-gray-200">{children}</td>
  ),
};

// Progress Accordion for showing execution steps
const ProgressAccordion: React.FC<{ progress: StructuredProgress }> = ({ progress }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const steps = progress.plan_steps || [];

  if (steps.length === 0) return null;

  const completedCount = steps.filter(
    (s) => s.status === 'completed' || s.status === 'skipped',
  ).length;

  return (
    <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-sm text-gray-600">
            Completed {completedCount} of {steps.length} steps
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 border-t border-gray-100 space-y-2">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-center gap-2">
              {step.status === 'completed' ? (
                <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              ) : step.status === 'skipped' ? (
                <div className="w-4 h-4 rounded-full bg-gray-300" />
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
              )}
              <span
                className={`text-sm ${step.status === 'completed' ? 'text-gray-700' : 'text-gray-400'}`}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const HealthcareChatViewer: React.FC<HealthcareChatViewerProps> = ({ session }) => {
  // Parse messages that might be a string (JSON), array, or undefined
  const parseMessages = (): ChatMessage[] => {
    const raw = session.messages;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as ChatMessage[];
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const messages = parseMessages();

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-[#E8FCFA] to-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Leaf className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
          <h4 className="font-semibold text-gray-900">
            {session.title || `Healthcare Chat Session #${session.id}`}
          </h4>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Started: {new Date(session.created_at).toLocaleString()}
          {session.updated_at !== session.created_at && (
            <span> | Last updated: {new Date(session.updated_at).toLocaleString()}</span>
          )}
        </p>
      </div>

      {/* Admin View Banner */}
      <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
        <p className="text-xs text-blue-600 flex items-center gap-1">
          <MessageSquare className="w-3 h-3" />
          Admin view - Healthcare Chat v2 conversation
        </p>
      </div>

      {/* Messages */}
      <div className="p-4 max-h-[600px] overflow-y-auto bg-[#FAFBFC]">
        {messages.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No messages in this conversation</p>
        ) : (
          <div className="space-y-5">
            {messages.map((msg, idx) => (
              <React.Fragment key={idx}>
                {/* Progress accordion shown BEFORE assistant message */}
                {msg.role === 'assistant' && msg.progress_snapshot && (
                  <div className="flex justify-start">
                    <div className="max-w-2xl w-full">
                      <ProgressAccordion progress={msg.progress_snapshot} />
                    </div>
                  </div>
                )}

                {/* Message bubble */}
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`rounded-2xl px-5 py-4 ${
                      msg.role === 'user'
                        ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                        : 'max-w-full bg-white border border-[#E5E7EB] text-[#374151] rounded-bl-md shadow-sm'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="max-w-none prose prose-sm">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}

                    {/* Personas cited */}
                    {msg.role === 'assistant' &&
                      msg.personas_cited &&
                      msg.personas_cited.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Personas cited: {msg.personas_cited.join(', ')}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
        {messages.length} message{messages.length !== 1 ? 's' : ''} in conversation
      </div>
    </div>
  );
};

export default HealthcareChatViewer;
